/**
 * 赏金任务状态机
 * 基于四阶段生命周期设计：投递期 → 开发期 → 审查期 → 结算(终态)
 * @see docs/grills/task-module-interaction-logic.md
 */

// ==================== 状态枚举 ====================

const bountyStatusValues = [
  "drafting",
  "bidding",
  "in_progress",
  "reviewing",
  "completed",
  "cancelled",
] as const;

export const BOUNTY_STATUS = {
  DRAFTING: "drafting",
  BIDDING: "bidding",
  IN_PROGRESS: "in_progress",
  REVIEWING: "reviewing",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
} as const;

export type BountyStatus = (typeof bountyStatusValues)[number];

// ==================== Outcome 枚举 ====================

const bountyOutcomeValues = [
  "expired",
  "failed_delivery",
  "abandoned",
  "publisher_cancel",
  null,
] as const;

export const BOUNTY_OUTCOME = {
  EXPIRED: "expired",
  FAILED_DELIVERY: "failed_delivery",
  ABANDONED: "abandoned",
  PUBLISHER_CANCEL: "publisher_cancel",
} as const;

export type BountyOutcome = (typeof bountyOutcomeValues)[number];

// ==================== 阶段枚举 ====================

export const BOUNTY_PHASE = {
  DRAFTING: "drafting",      // 草稿阶段
  BIDDING: "bidding",        // 投递期
  DEVELOPMENT: "development", // 开发期
  REVIEWING: "reviewing",    // 审查期
  TERMINAL: "terminal",      // 终态
} as const;

export type BountyPhase = (typeof BOUNTY_PHASE)[keyof typeof BOUNTY_PHASE];

// ==================== 状态转换规则 ====================

const allowedTransitions: Record<BountyStatus, readonly BountyStatus[]> = {
  drafting: ["bidding", "cancelled"],
  bidding: ["in_progress", "cancelled"],
  in_progress: ["reviewing", "cancelled"],
  reviewing: ["completed", "in_progress", "cancelled"],
  completed: [], // 终态
  cancelled: [], // 终态
};

// ==================== 状态转换函数 ====================

/**
 * 检查状态转换是否合法
 */
export function canTransitionBountyStatus(
  currentStatus: BountyStatus,
  nextStatus: BountyStatus,
): boolean {
  return allowedTransitions[currentStatus].includes(nextStatus);
}

/**
 * 执行状态转换，非法时抛出错误
 */
export function transitionBountyStatus(
  currentStatus: BountyStatus,
  nextStatus: BountyStatus,
): BountyStatus {
  if (!canTransitionBountyStatus(currentStatus, nextStatus)) {
    throw new Error(
      `非法的赏金任务状态转换: ${currentStatus} -> ${nextStatus}`,
    );
  }

  return nextStatus;
}

// ==================== 辅助函数 ====================

/**
 * 获取当前状态所属的阶段
 * @returns 'drafting' | 'bidding' | 'development' | 'reviewing' | 'terminal'
 */
export function getBountyPhase(status: BountyStatus): BountyPhase {
  switch (status) {
    case "drafting":
      return BOUNTY_PHASE.DRAFTING;
    case "bidding":
      return BOUNTY_PHASE.BIDDING;
    case "in_progress":
      return BOUNTY_PHASE.DEVELOPMENT;
    case "reviewing":
      return BOUNTY_PHASE.REVIEWING;
    case "completed":
    case "cancelled":
      return BOUNTY_PHASE.TERMINAL;
    default:
      throw new Error(`未知的状态: ${status}`);
  }
}

/**
 * 判断是否为终态
 */
export function isTerminalStatus(status: BountyStatus): boolean {
  return status === "completed" || status === "cancelled";
}

/**
 * 判断当前状态是否可以「结束投递并选标」
 * (发布者在 bidding 状态可以选标)
 */
export function canPublishSelectBid(status: BountyStatus): boolean {
  return status === "bidding";
}

/**
 * 判断当前状态是否可以提交交付物
 * @param status 当前状态
 * @param reviewRound 当前审查轮次 (可选)
 * @returns 是否可以提交
 */
export function canSubmitDelivery(
  status: BountyStatus,
  reviewRound?: number,
): boolean {
  if (status === "in_progress") {
    return true;
  }
  if (status === "reviewing") {
    // 审查期最多 3 轮，超过则不能再提交
    const round = reviewRound ?? 0;
    return round < 3;
  }
  return false;
}

/**
 * 获取状态的中文标签
 * @param status 主状态
 * @param outcome 终态分类 (可选)
 * @returns 中文显示标签
 */
export function getStatusLabel(
  status: BountyStatus,
  outcome?: BountyOutcome,
): string {
  if (status === "cancelled" && outcome) {
    switch (outcome) {
      case "expired":
        return "已过期";
      case "failed_delivery":
        return "交付失败";
      case "abandoned":
        return "已放弃";
      case "publisher_cancel":
        return "已取消";
    }
  }

  switch (status) {
    case "drafting":
      return "草稿";
    case "bidding":
      return "投递期";
    case "in_progress":
      return "开发期";
    case "reviewing":
      return "审查期";
    case "completed":
      return "已完成";
    case "cancelled":
      return "已取消";
    default:
      return "未知状态";
  }
}

/**
 * 获取状态的颜色标识 (用于 UI 展示)
 */
export function getStatusColor(status: BountyStatus): string {
  switch (status) {
    case "drafting":
      return "#94A3B8";
    case "bidding":
      return "#6366F1";
    case "in_progress":
      return "#F59E0B";
    case "reviewing":
      return "#F97316";
    case "completed":
      return "#10B981";
    case "cancelled":
      return "#EF4444";
    default:
      return "#94A3B8";
  }
}

// ==================== 常量定义 ====================

/** 最大审查轮次 */
export const MAX_REVIEW_ROUNDS = 3;

/** 默认平台费率 (10%) */
export const DEFAULT_PLATFORM_FEE_RATE = 0.1;

/** 审查期取消时开发者补偿比例 (30%) */
export const PARTIAL_SETTLE_DEVELOPER_SHARE = 0.3;

/** 初始信誉分数 */
export const INITIAL_REPUTATION_SCORE = 100;

/** 放弃任务扣分 */
export const REPUTATION_PENALTY_ABANDON = 15;

/** 信誉冻结阈值 */
export const REPUTATION_FREEZE_THRESHOLD = 80;
